import copy from '../copy/en/intake.json' with { type: 'json' };
import { formatCopy } from '../copy/format.js';

export interface NativeReviewAnswer {
  key: string;
  stage: number;
  label: string;
  required: boolean;
  value: unknown;
}

const groups = [
  [
    [copy.practiceIdentity, ['practiceName', 'business.legalName', 'business.practiceName', 'business.practiceType', 'business.ownershipType']],
    [copy.primaryLocation, ['locations.addressLine1', 'locations.addressLine2', 'locations.city', 'locations.region', 'locations.postalCode', 'locations.countryCode', 'locations.reportedLocationCount']],
  ],
  [
    [copy.doctorContact, ['people.doctorName', 'people.contactEmail', 'people.officePhone', 'people.mobilePhone']],
    [copy.mainPracticeContact, ['people.mainContactName', 'people.jobTitle', 'people.mainContactEmail']],
    [copy.authorizedSigner, ['signerName', 'email', 'signerTitle', 'signerCapacity']],
  ],
  [
    [copy.officeHours, ['operations.daysOpen', 'operations.typicalHours']],
    [copy.team, ['people.fullTimeEmployeeCount', 'people.ownerDentistCount', 'people.dentistCount', 'people.hygienistCount']],
    [copy.patientsCapacity, ['operations.monthlyPatientVisits', 'operations.activePatientCount', 'operations.operatories']],
    [copy.technology, ['operations.practiceSoftware', 'operations.intraoralScanner']],
  ],
  [
    [copy.businessDetails, ['business.entityType', 'business.yearsInPractice', 'business.npi', 'business.website']],
    [copy.financialSnapshot, ['financial.practiceRevenue', 'financial.annualCardVolume', 'financial.processingFee']],
    [copy.facilityPlans, ['business.facilityTenure', 'interests.acquisitionIntent', 'interests.transitionIntent']],
  ],
  [[copy.servicesOffered, ['operations.procedures', 'operations.insuranceCarriers']]],
] satisfies [string, string[]][][];

const element = <K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};
const populated = (value: unknown): boolean => Array.isArray(value)
  ? value.length > 0
  : value !== undefined && value !== null && String(value).trim() !== '';

/** Display only the captured answers. No controls, storage or live form reads. */
export function renderNativeReviewAnswers(target: HTMLElement, answers: readonly NativeReviewAnswer[]): void {
  const titles = [copy.practice, copy.people, copy.operations, copy.business, copy.services];
  const contexts = [copy.reviewPracticeContext, copy.reviewPeopleContext, copy.reviewOperationsContext, copy.reviewBusinessContext, copy.reviewServicesContext];
  const cards = titles.map((title, stage) => {
    const card = element('details', 'field-group review-group native-review-card');
    card.dataset.reviewStage = String(stage);
    const summary = element('summary', 'native-review-summary');
    summary.tabIndex = 0;
    summary.append(element('span', 'native-review-title', title));
    const content = element('div', 'native-review-content');
    card.append(summary, content);
    const heading = element('div', 'native-review-heading');
    const editText = formatCopy(copy.editSection, { section: title.toLowerCase() });
    const edit = element('button', 'btn btn-ghost btn-small native-review-edit');
    edit.type = 'button';
    edit.dataset.editStage = String(stage);
    const pencil = element('span', 'native-review-pencil', '✎');
    pencil.setAttribute('aria-hidden', 'true');
    edit.append(pencil, element('span', '', editText));
    heading.append(element('p', 'native-review-context', contexts[stage]), edit);
    content.append(heading);
    for (const [title, keys] of groups[stage]!) {
      const entries = keys.map(key => answers.find(answer => answer.stage === stage && answer.key === key))
        .filter((answer): answer is NativeReviewAnswer => answer !== undefined && (answer.required || populated(answer.value)));
      if (!entries.length) continue;
      const section = element('div', 'native-review-section');
      section.append(element('h3', 'native-review-section-title', title));
      const list = element('dl', 'native-review-answers');
      for (const answer of entries) {
        const row = element('div', `native-review-answer${answer.key === 'operations.typicalHours' ? ' native-review-answer-wide' : ''}`);
        row.dataset.reviewField = answer.key;
        const value = element('dd', 'native-review-value');
        if (!populated(answer.value)) value.append(element('span', 'native-review-missing', copy.reviewNotProvided));
        else if (Array.isArray(answer.value)) {
          const items = element('ul', 'native-review-values');
          for (const item of answer.value) items.append(element('li', '', String(item)));
          value.append(items);
        } else value.textContent = String(answer.value);
        row.append(element('dt', 'native-review-label', answer.label), value);
        list.append(row);
      }
      section.append(list);
      content.append(section);
    }
    return card;
  });
  target.replaceChildren(element('p', 'group-intro native-review-intro', copy.reviewIntroduction), ...cards);
}
